/**
 * Trainer-facing report editor for a single training-session booking. Loads any
 * existing report, lets the trainer edit a DRAFT (times, focus areas, progress
 * rating, homework, notes, photos), Save draft (create/update) or Publish (locks
 * it, owner-visible). Read-only once PUBLISHED. Esc / backdrop closes.
 */
import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { form_error_message } from '@/lib/forms';
import { use_training_report, use_save_training_report } from '@/features/training-report/hooks';
import type { Booking } from '@/features/bookings/types';

const field_class =
  'flex h-11 w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15 disabled:opacity-60';

/** ISO → the value a <input type="datetime-local"> expects (local wall-clock). */
function iso_to_local(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TrainingReportModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const report_q = use_training_report(booking.id);
  const { save_m, publish_m } = use_save_training_report(booking.id);

  const [actual_start, set_actual_start] = useState('');
  const [actual_end, set_actual_end] = useState('');
  const [focus_areas, set_focus_areas] = useState('');
  const [progress_rating, set_progress_rating] = useState('');
  const [homework, set_homework] = useState('');
  const [notes, set_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const report = report_q.data ?? null;
  const exists = report !== null;
  const published = report?.status === 'PUBLISHED';
  const read_only = published;

  useEffect(() => {
    if (report_q.isLoading) return;
    set_actual_start(iso_to_local(report?.actual_start ?? null));
    set_actual_end(iso_to_local(report?.actual_end ?? null));
    set_focus_areas(report?.focus_areas ?? '');
    set_progress_rating(report?.progress_rating != null ? String(report.progress_rating) : '');
    set_homework(report?.homework ?? '');
    set_notes(report?.notes ?? '');
  }, [report, report_q.isLoading]);

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  function build_payload() {
    return {
      actual_start: actual_start ? new Date(actual_start).toISOString() : undefined,
      actual_end: actual_end ? new Date(actual_end).toISOString() : undefined,
      focus_areas: focus_areas.trim() || undefined,
      progress_rating: progress_rating.trim() ? Number(progress_rating) : undefined,
      homework: homework.trim() || undefined,
      notes: notes.trim() || undefined,
    };
  }

  async function save_draft() {
    set_error(null);
    try {
      await save_m.mutateAsync(build_payload());
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the report.'));
    }
  }

  async function save_and_publish() {
    set_error(null);
    try {
      await save_m.mutateAsync(build_payload());
      await publish_m.mutateAsync();
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not publish the report.'));
    }
  }

  const busy = save_m.isPending || publish_m.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="training-report-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-xl rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="font-heading text-lg font-bold">Session report</h2>
            <p className="text-xs text-muted-foreground">{published ? 'Published — read only' : exists ? 'Draft — editable' : 'New draft'}</p>
          </div>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="training-report-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {report_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Started</Label>
                <input type="datetime-local" className={field_class} value={actual_start} onChange={(e) => set_actual_start(e.target.value)} disabled={read_only} data-testid="tr-start" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Ended</Label>
                <input type="datetime-local" className={field_class} value={actual_end} onChange={(e) => set_actual_end(e.target.value)} disabled={read_only} data-testid="tr-end" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Progress (1–5)</Label>
                <Input type="number" step="1" min="1" max="5" value={progress_rating} onChange={(e) => set_progress_rating(e.target.value)} disabled={read_only} data-testid="tr-progress" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Focus areas</Label>
                <Input value={focus_areas} onChange={(e) => set_focus_areas(e.target.value)} disabled={read_only} data-testid="tr-focus" placeholder="Skills / commands worked on" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Homework</Label>
                <textarea className={`${field_class} h-16 resize-y`} value={homework} onChange={(e) => set_homework(e.target.value)} disabled={read_only} data-testid="tr-homework" placeholder="What should the owner practise?" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Notes</Label>
                <textarea className={`${field_class} h-20 resize-y`} value={notes} onChange={(e) => set_notes(e.target.value)} disabled={read_only} data-testid="tr-notes" placeholder="How did the session go?" />
              </div>
            </div>

            {/* Photos — available once a draft exists (needs the report id as owner). */}
            {exists && report && !read_only && (
              <ImageGalleryEditor owner_type="TRAINING_REPORT" owner_id={report.id} label="Session photos" />
            )}
            {published && report && report.photos.length > 0 && (
              <p className="text-xs text-muted-foreground">{report.photos.length} photo(s) shared with the owner.</p>
            )}

            {error && <p className="text-sm text-destructive" data-testid="training-report-error">{error}</p>}

            {!read_only && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={save_draft} disabled={busy} data-testid="training-report-save">
                  {save_m.isPending && !publish_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save draft
                </Button>
                <Button variant="gradient" onClick={save_and_publish} disabled={busy} data-testid="training-report-publish">
                  {publish_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Publish
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
